import { type MobileProblem, parseIndianMobile } from '@oneapp/shared';
import { type FormEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { RECAPTCHA_CONTAINER_ID, SIGN_IN_ERROR_KEYS } from '../auth/messages';
import { pauseSignIn, signInFlow, signInPausedUntil } from '../auth/signInFlow';
import { Page, PageTitle, PhoneField } from '../components/forms';
import { Button, Callout } from '../components/ui';
import { isSignInConfigured, sendSignInCode, toSignInError } from '../lib/firebase';
import { currentLanguage } from '../lib/i18n';
import { SignInPaused } from './SignInPaused';

const PROBLEM_KEYS: Record<MobileProblem, string> = {
  empty: 'signIn.errorEmpty',
  length: 'signIn.errorLength',
  start: 'signIn.errorStart',
};

/** A2 · Mobile number: sign-in and sign-up are the same step. */
export function SignIn() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const input = useRef<HTMLInputElement>(null);
  const [digits, setDigits] = useState('');
  const [fieldError, setFieldError] = useState<string>();
  const [failure, setFailure] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [pausedUntil, setPausedUntil] = useState(() => signInPausedUntil());

  if (pausedUntil) return <SignInPaused until={pausedUntil} onBack={() => navigate('/welcome')} />;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setFailure(undefined);
    const parsed = parseIndianMobile(digits);
    if (!parsed.ok) {
      setFieldError(t(PROBLEM_KEYS[parsed.problem]));
      input.current?.focus();
      return;
    }
    setFieldError(undefined);
    if (!isSignInConfigured()) {
      setFailure(t('signIn.notConfigured'));
      return;
    }
    setBusy(true);
    try {
      const phoneE164 = `+91${parsed.digits}`;
      const confirmation = await sendSignInCode(phoneE164, RECAPTCHA_CONTAINER_ID, currentLanguage());
      signInFlow.start(phoneE164, confirmation);
      navigate('/sign-in/code');
    } catch (error) {
      const problem = toSignInError(error);
      if (problem.code === 'too-many-requests') setPausedUntil(pauseSignIn());
      else setFailure(t(SIGN_IN_ERROR_KEYS[problem.code]));
      setBusy(false);
    }
  };

  return (
    <Page
      bar={t('signIn.bar')}
      back={{ label: t('signIn.back'), onClick: () => navigate('/welcome') }}
      labelledBy="sign-in-title"
      footer={
        <Button type="submit" form="sign-in-form" busy={busy} busyLabel={t('signIn.sending')}>
          {t('signIn.getCode')}
        </Button>
      }
    >
      <PageTitle id="sign-in-title" title={t('signIn.title')}>
        {t('signIn.lede')}
      </PageTitle>
      <form id="sign-in-form" onSubmit={submit} noValidate>
        <PhoneField
          label={t('signIn.mobile')}
          countryLabel={t('signIn.countryCode')}
          digits={digits}
          onChange={(next) => {
            setDigits(next);
            if (fieldError) setFieldError(undefined);
          }}
          error={fieldError}
          inputRef={input}
        />
      </form>
      {failure ? (
        <Callout tone="error" icon="alert-triangle" role="alert">
          {failure}
        </Callout>
      ) : null}
      <Callout icon="bell">{t('signIn.reminders')}</Callout>
      <div id={RECAPTCHA_CONTAINER_ID} />
    </Page>
  );
}
